// src/components/layout/Sidebar.jsx - Clean AI Sentry Navigation
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';

const Sidebar = ({ mobile = false, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasPermission } = useAuth();
  const [useCasesOpen, setUseCasesOpen] = useState(false);
  const [axiomOpen, setAxiomOpen] = useState(false);
  const [intelOpen, setIntelOpen] = useState(false);
  const [lightMode, setLightMode] = useState(false);
  const [healthStatus, setHealthStatus] = useState(null);

  useEffect(() => {
    const API = process.env.REACT_APP_API_URL || "";
    fetch(`${API}/health`)
      .then(r => r.json())
      .then(d => setHealthStatus(d.overall))
      .catch(() => {});
    const interval = setInterval(() => {
      fetch(`${API}/health`)
        .then(r => r.json())
        .then(d => setHealthStatus(d.overall))
        .catch(() => {});
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  // Pages that have light mode versions
  const lightModeRoutes = {
    '/sovereign-edge': '/sovereign-edge-light',
    '/topology': '/topology-light',
    '/ai-sentry': '/ai-sentry-light',
  };

  const darkModeRoutes = {
    '/sovereign-edge-light': '/sovereign-edge',
    '/topology-light': '/topology',
    '/ai-sentry-light': '/ai-sentry',
  };

  // Handle light/dark toggle
  const handleModeToggle = () => {
    const newMode = !lightMode;
    setLightMode(newMode);
    
    // If currently on a page with a light/dark variant, switch to it
    const currentPath = location.pathname;
    if (newMode && lightModeRoutes[currentPath]) {
      navigate(lightModeRoutes[currentPath]);
    } else if (!newMode && darkModeRoutes[currentPath]) {
      navigate(darkModeRoutes[currentPath]);
    }
  };

  // Get the correct href based on light/dark mode
  const getHref = (baseHref) => {
    if (lightMode && lightModeRoutes[baseHref]) {
      return lightModeRoutes[baseHref];
    }
    return baseHref;
  };

  // Security section items
  const securityNav = [
    {
      name: 'AI Sentry',
      href: '/ai-sentry',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      name: 'Security Policies',
      href: '/policies',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
    },
    {
      name: 'AI Insights',
      href: '/chat',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      ),
    },
  ];

  // Platform section items
  const platformNav = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
      ),
    },
    {
      name: 'Network Topology',
      href: '/topology',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
        </svg>
      ),
    },
    {
      name: 'Edge Nodes',
      href: '/edge-nodes',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      ),
    },
    {
      name: 'Security Events',
      href: '/events',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      ),
    },
  ];

  // Vertical Solutions (collapsible)
  const useCasesNav = [
    { name: 'Fleet Sentry', href: '/fleet-sentry', icon: <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h8m-8 4h8m-6 4h4M5 3h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2z" /></svg> },
    { name: 'DC Sentry', href: '/dc-sentry', icon: <span className="text-lg">🖥️</span> },
    { name: 'Safety Sentry', href: '/safety-sentry', icon: <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg> },
    { name: 'Printer Sentry', href: '/printer-sentry', icon: <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg> },
    { name: 'HP Executive', href: '/hp-executive', icon: <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg> },
    { name: 'HP Revenue Control', href: '/hp-revenue-control', icon: <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> },
    { name: 'Pipeline Sentry', href: '/pipeline-sentry', icon: <span className="text-lg">🛢️</span> },
    { name: 'Maritime Sentry', href: '/maritime-sentry', icon: <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg> },
    { name: 'Building Sentry', href: '/building-sentry', icon: <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg> },
    { name: 'Construction Sentry', href: '/construction-sentry', icon: <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v4m-2-2h4" /></svg> },
    { name: 'Sovereign Edge', href: '/sovereign-edge', icon: <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>, badge: 'Palo Alto' },
    { name: 'Sovereign Edge Medical', href: '/sovereign-edge-medical', icon: <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg> },
    { name: 'Sovereign Edge DC', href: '/sovereign-edge-dc', icon: <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg> },
  ];

  const isActive = (href) => {
    const currentPath = location.pathname;
    // Check both light and dark versions
    if (href === '/sovereign-edge') {
      return currentPath === '/sovereign-edge' || currentPath === '/sovereign-edge-light';
    }
    if (href === '/sovereign-edge-medical') {
      return currentPath === '/sovereign-edge-medical';
    }
    if (href === '/sovereign-edge-dc') {
      return currentPath === '/sovereign-edge-dc';
    }
    if (href === '/topology') {
      return currentPath === '/topology' || currentPath === '/topology-light';
    }
    if (href === '/ai-sentry') {
      return currentPath === '/ai-sentry' || currentPath === '/ai-sentry-light';
    }
    return currentPath.startsWith(href);
  };

  const renderNavItem = (item) => {
    const active = isActive(item.href);
    const href = getHref(item.href);
    return (
      <Link
        key={item.name}
        to={href}
        onClick={mobile ? onClose : undefined}
        className={`group flex items-center px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${
          active
            ? 'bg-blue-700 text-white'
            : 'text-gray-300 hover:bg-gray-800 hover:text-white'
        }`}
      >
        <span className={`mr-3 flex-shrink-0 ${active ? 'text-white' : 'text-gray-400 group-hover:text-white'}`}>
          {item.icon}
        </span>
        <span className="flex-1">{item.name}</span>
        {item.badge && (
          <span className="ml-2 px-1.5 py-0.5 text-[9px] font-bold rounded border bg-orange-500/20 text-orange-400 border-orange-500/30">
            {item.badge}
          </span>
        )}
      </Link>
    );
  };

  const Divider = () => (
    <div className="my-2 mx-4 border-t border-gray-800" />
  );

  return (
    <div className="flex flex-col h-full bg-gray-900">
      {/* Logo/Header */}
      <div className="flex items-center h-16 px-6 border-b border-gray-800">
        {mobile && (
          <button
            type="button"
            className="mr-3 p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-white"
            onClick={onClose}
          >
            <span className="sr-only">Close sidebar</span>
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        <div className="flex items-center">
          <div className="flex-shrink-0 w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
            <span className="text-white font-bold text-lg">⚡</span>
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">AI Sentry</h1>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
        {/* System Health */}
        <Link
          to="/health"
          onClick={mobile ? onClose : undefined}
          className={`group flex items-center px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${
            location.pathname === '/health'
              ? 'bg-blue-700 text-white'
              : 'text-gray-300 hover:bg-gray-800 hover:text-white'
          }`}
        >
          <span className={`mr-3 flex-shrink-0 ${location.pathname === '/health' ? 'text-white' : 'text-gray-400 group-hover:text-white'}`}>
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </span>
          <span className="flex-1">System Health</span>
          {healthStatus && (
            <span
              className="ml-2 w-2 h-2 rounded-full flex-shrink-0"
              style={{
                backgroundColor: healthStatus === 'ok' ? '#1D9E75'
                  : healthStatus === 'warning' ? '#BA7517' : '#E24B4A'
              }}
            />
          )}
        </Link>

        <Divider />

        {/* Security items */}
        {securityNav.map(renderNavItem)}
        
        <Divider />
        
        {/* Platform items */}
        {platformNav.map(renderNavItem)}
        
        <Divider />
        
        {/* Vertical Solutions (Collapsible) */}
        <button
          onClick={() => setUseCasesOpen(!useCasesOpen)}
          className="w-full group flex items-center px-4 py-2.5 text-sm font-medium rounded-lg transition-colors text-gray-300 hover:bg-gray-800 hover:text-white"
        >
          <span className="mr-3 flex-shrink-0 text-gray-400 group-hover:text-white">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </span>
          <span className="flex-1 text-left">Vertical Solutions</span>
          <span className="text-[10px] text-gray-500 mr-2">{useCasesNav.length}</span>
          <svg 
            className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${useCasesOpen ? 'rotate-180' : ''}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {/* Collapsible Use Cases */}
        <div className={`overflow-hidden transition-all duration-200 ${useCasesOpen ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="pl-4 space-y-0.5">
            {useCasesNav.map((item) => {
              const active = isActive(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={mobile ? onClose : undefined}
                  className={`group flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    active
                      ? 'bg-blue-700/50 text-white'
                      : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                  }`}
                >
                  <span className={`mr-3 flex-shrink-0 ${active ? 'text-white' : 'text-gray-500 group-hover:text-white'}`}>
                    {item.icon}
                  </span>
                  <span className="flex-1">{item.name}</span>
                </Link>
              );
            })}
          </div>
        </div>

        {user?.role === 'admin' && (
        <>
        <Divider />

        {/* Intelligence Data (collapsible) */}
        <button
          onClick={() => setIntelOpen(!intelOpen)}
          className="w-full group flex items-center px-4 py-2.5 text-sm font-semibold text-gray-300 hover:bg-gray-800 rounded-lg transition-colors"
        >
          <span className="mr-3 flex-shrink-0 text-gray-400 group-hover:text-white">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
            </svg>
          </span>
          <span className="flex-1 text-left">Intelligence Data</span>
          <span className="text-[10px] text-gray-500 mr-2">4</span>
          <svg
            className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${intelOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        <div className={`overflow-hidden transition-all duration-200 ${intelOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="pl-4 space-y-0.5">
            {[
              { name: 'Data Sources',      href: '/intelligence/sources',  icon: <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" /></svg> },
              { name: 'Computation Audit',  href: '/intelligence/audit',    icon: <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg> },
              { name: 'National Markets',   href: '/intelligence/markets',  icon: <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> },
              { name: 'Raw Factor Data',    href: '/signals/raw',           icon: <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" /></svg>, perm: 'signals' },
            ].filter(item => !item.perm || hasPermission(item.perm)).map((item) => {
              const active = isActive(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={mobile ? onClose : undefined}
                  className={`group flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    active
                      ? 'bg-blue-700/50 text-white'
                      : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                  }`}
                >
                  <span className={`mr-3 flex-shrink-0 ${active ? 'text-white' : 'text-gray-500 group-hover:text-white'}`}>
                    {item.icon}
                  </span>
                  <span className="flex-1">{item.name}</span>
                </Link>
              );
            })}
          </div>
        </div>

        <Divider />

        {/* Axiom Suite expander */}
        <button
          onClick={() => setAxiomOpen(!axiomOpen)}
          className="w-full flex items-center px-4 py-2.5 text-sm font-semibold text-gray-300 hover:bg-gray-800 rounded-lg transition-colors"
        >
          <span className="mr-3 text-lg">🔮</span>
          <span className="flex-1 text-left">Axiom Suite</span>
          <span className="text-[10px] text-gray-500 mr-2">19</span>
          <svg
            className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${axiomOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        <div className={`overflow-hidden transition-all duration-200 ${axiomOpen ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="pl-4 space-y-0.5">
            {[

              { name: 'Briefings',           href: '/briefings',           icon: <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg> },
              { name: 'Project Flow V2',     href: '/project-oracle-v2',   icon: <span className="text-lg">🔮</span> },
              { name: 'DC Sentry',           href: '/dc-sentry',           icon: <span className="text-lg">🖥️</span> },
              { name: 'DC Fleet',            href: '/dc-fleet',            icon: <span className="text-lg">🏢</span> },
              { name: 'Axiom Stakeholder',   href: '/axiom-stakeholder',   icon: <span className="text-lg">👥</span> },
              { name: 'Axiom Policies',      href: '/axiom-policies',      icon: <span className="text-lg">📜</span> },
              { name: 'Axiom Briefing',      href: '/axiom-briefing',      icon: <span className="text-lg">📋</span> },
              { name: 'Axiom Story',         href: '/axiom-story',         icon: <span className="text-lg">📖</span> },
              { name: 'Platform Story',     href: '/platform-story',     icon: <span className="text-lg">📄</span> },
              { name: 'Aligned Sales Wall', href: '/aligned',            icon: <span className="text-lg">🏗️</span> },
              { name: 'Aligned (Mobile)',  href: '/m/aligned',          icon: <span className="text-lg">📱</span> },
              { name: 'Procurement V2',  href: '/procurement-story-v2', icon: <span className="text-lg">🛒</span>, perm: 'procurement' },
              { name: 'Procurement Ops', href: '/procurement',          icon: <span className="text-lg">📋</span>, perm: 'procurement' },
              { name: 'Production Planner', href: '/procurement/planner',   icon: <span className="text-lg">📦</span>, perm: 'procurement' },
              { name: 'Project Financials', href: '/procurement/financials', icon: <span className="text-lg">💰</span>, perm: 'procurement' },
              { name: 'Design Management', href: '/procurement/design',     icon: <span className="text-lg">📐</span>, perm: 'procurement' },
              { name: 'Project Schedule',  href: '/procurement/schedule',  icon: <span className="text-lg">📅</span>, perm: 'procurement' },
              { name: 'Documentation',    href: '/procurement/documentation', icon: <span className="text-lg">📝</span>, perm: 'procurement' },
              { name: 'Cx Look Ahead',   href: '/procurement/cx-lookahead', icon: <span className="text-lg">🔭</span>, perm: 'procurement' },
              { name: 'Trading Signals',   href: '/signals',            icon: <span className="text-lg">📈</span>, perm: 'signals' },
            ].filter(item => !item.perm || hasPermission(item.perm)).map((item) => {
              const active = isActive(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={mobile ? onClose : undefined}
                  className={`group flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    active
                      ? 'bg-blue-700/50 text-white'
                      : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                  }`}
                >
                  <span className={`mr-3 flex-shrink-0 ${active ? 'text-white' : 'text-gray-500 group-hover:text-white'}`}>
                    {item.icon}
                  </span>
                  <span className="flex-1">{item.name}</span>
                </Link>
              );
            })}
          </div>
        </div>

        <Divider />

        {/* Fleet administration (admin only) */}
        {[
          { name: 'Edge Agents',         href: '/edge-agents', icon: <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg> },
        ].map(renderNavItem)}

        <Divider />

        {/* AI Terminals (admin only) */}
        {[
          { name: 'Forge (Code)',        href: '/forge',   icon: <span className="text-lg">&#9000;</span> },
          { name: 'Stratos (Strategy)',  href: '/stratos', icon: <span className="text-lg">&#9733;</span> },
        ].map(renderNavItem)}
        </>
        )}

      </nav>

      {/* User Footer with Light/Dark Toggle */}
      <div className="flex-shrink-0 border-t border-gray-800 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center">
                <span className="text-gray-300 text-xs">
                  {user?.name?.split(' ').map(n => n[0]).join('') || 'SA'}
                </span>
              </div>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-white">{user?.name || 'System Administrator'}</p>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-emerald-400 rounded-full mr-2"></div>
                <p className="text-xs text-gray-400">Active</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Light/Dark Toggle */}
            <button
              onClick={handleModeToggle}
              className={`p-2 rounded-lg transition-colors ${
                lightMode 
                  ? 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30' 
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
              title={lightMode ? 'Switch to Dark' : 'Switch to Light'}
            >
              {lightMode ? (
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>
            
            {/* Account / Change password */}
            <Link
              to="/account"
              className="p-2 rounded-lg text-gray-400 hover:text-blue-400 hover:bg-gray-800 transition-colors"
              title="Account"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </Link>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-gray-800 transition-colors"
              title="Logout"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
